import { useEffect, useMemo } from 'react';
import {
  DefaultNodeOptions,
  StringNode,
  widget,
  WidgetPlugin,
} from '@sagold/react-json-editor';

import { FormField } from '../../../components/formfield/FormField';
import { ReadOnlyInput } from '../../../components/readonly-input/ReadOnlyInput';
import { SingleSelect } from '../../../components/selects/SingleSelect';
import { useErrorTransformation } from '../widgetUtils';

export type SelectOptions = {
  clearable?: boolean;
  loading?: boolean;
  showErrors?: boolean;
  tableLayout?: boolean;
} & DefaultNodeOptions;

export const SelectWidget = widget<StringNode<SelectOptions>, string | null>(
  ({ node, options, setValue }) => {
    const hasError = node.errors.length > 0;
    const titles = (node.schema.options?.enum as string[]) ?? [];
    const errors = useErrorTransformation(node.errors);
    const enumValues = useMemo(
      () =>
        // Filter null from schema.enum, as it should not appear as option in the single select.
        // It has to be present as allowed value in the schema when the selection is not required
        // to pass the validation in case nothing is selected.
        (node.schema.enum?.filter((v) => v != null) || []) as string[],
      [node.schema.enum]
    );

    // Reset initial value if it was set to a string not contained in the enum list
    useEffect(() => {
      if (node.value != null && !enumValues.includes(node.value)) {
        setValue(null);
      }
    }, [enumValues, node.value, setValue]);

    return (
      <FormField
        widgetType="select"
        node={node}
        errors={errors}
        options={options}
        tableLayout={options.tableLayout}
      >
        {options.readOnly ? (
          <ReadOnlyInput
            label={options.title}
            value={node.value}
            tableLayout={options.tableLayout}
          />
        ) : (
          <SingleSelect
            data={enumValues.map((value, index) => ({
              label: titles[index] ?? value,
              value,
            }))}
            label={options.title}
            placeholder={options.placeholder}
            error={options.showErrors !== false && hasError}
            clearable={options.clearable === true}
            required={options.required}
            disabled={options.disabled}
            loading={options.loading}
            readOnly={options.readOnly}
            value={node.value ?? null}
            tableLayout={options.tableLayout}
            onChange={(value) => setValue(value)}
          />
        )}
      </FormField>
    );
  }
);

export const SelectWidgetPlugin: WidgetPlugin = {
  id: 'select-widget',
  use: (node) =>
    (node.schema.type === 'string' ||
      (Array.isArray(node.schema.type) &&
        node.schema.type.includes('string'))) &&
    Array.isArray(node.schema.enum),
  Widget: SelectWidget,
};
